import { useState, useEffect } from "react";
import { useOutletContext, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Play, Square, LineChart, History, Info, TriangleAlert, ArrowRight,
  Target, TrendingDown, ShieldAlert, ShieldCheck, Percent, Cpu, Loader2, Crown, Check, BadgeCheck,
} from "lucide-react";
import { AISoundCard } from "@/components/AISoundCard";
import { NewsBell } from "@/components/NewsBell";

const BOT_AVATAR = "https://static.prod-images.emergentagent.com/jobs/0c40222b-65cc-42fc-9117-09f0a7b6df59/images/beda8625d87f4cb78a04f33d14ef71520eb3fdaa5585e39b6568a20b616c3ff7.png";
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/0c40222b-65cc-42fc-9117-09f0a7b6df59/images/db2a5b0d7d37669d42cc8fa550c45fcd228d669493d0bd407890dde24e636237.png";

function SideAction({ icon: Icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="flex flex-col items-center gap-1.5 text-white/90 hover:text-white transition-colors focus:outline-none w-16">
      <Icon className="h-5 w-5" />
      <span className="font-heading text-xs font-semibold tracking-wide">{label}</span>
    </button>
  );
}

function Metric({ icon: Icon, label, value, tone }) {
  const color = tone === "up" ? "text-[#22e07b]" : tone === "down" ? "text-[#ff5b5b]" : "text-white";
  return (
    <div className="bg-[#0A1120] p-4 border border-[#14233d] rounded-2xl">
      <div className="flex items-center gap-2 text-[#5f7ba6] mb-2">
        <Icon className="h-4 w-4" />
        <span className="font-mono-term text-[10px] uppercase tracking-widest">{label}</span>
      </div>
      <div className={`font-heading text-xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

export default function HomeDash() {
  const { account, bots, loading, refreshAll, user } = useOutletContext();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const bot = bots[0];
  const license = user?.license;
  const [daily, setDaily] = useState(null);
  const [safety, setSafety] = useState(null);

  useEffect(() => {
    let alive = true;
    const fetchDaily = async () => {
      try { const { data } = await api.get("/bots/daily-report"); if (alive) setDaily(data); } catch { /* ignore */ }
      try { const { data } = await api.get("/summary/today"); if (alive) setSafety(data); } catch { /* ignore */ }
    };
    fetchDaily();
    const t = setInterval(fetchDaily, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const toggle = async () => {
    if (!bot) return;
    setBusy("toggle");
    try {
      const { data } = await api.post(`/bots/${bot.id}/toggle`);
      toast.success(data.status === "RUNNING" ? "AutoTrade started" : "Bot stopped — managing open trades");
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const addBot = async () => {
    setBusy("add");
    try { await api.post("/bots"); toast.success("Smart Scalper Fx added"); await refreshAll(); }
    finally { setBusy(null); }
  };

  const openLogs = () => {
    if (bot?.status === "RUNNING") navigate("/app/ai-activity");
    else toast.info("Start the bot to view live Live logs");
  };

  const reconnect = async () => {
    setBusy("reconnect");
    try {
      await api.post("/account/link");
      toast.success("MT5 rekonekte ✓");
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) ||
        "Pa t ka konekte. Tcheke non sèvè a EGZAK + MASTER password ou.");
    } finally { setBusy(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="h-6 w-6 animate-spin text-[#1C8CFF]" /></div>;

  const running = bot?.status === "RUNNING";
  const isProp = account?.type === "propfirm";
  const mt5Connected = !!(account && account.metaapi_account_id && (account.metaapi_status === "linked" || account.status === "connected"));
  const avatar = bot?.avatar || BOT_AVATAR;
  const avatarSrc = avatar?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${avatar}` : avatar;
  const tier = license?.active
    ? (license.plan_name ? license.plan_name.replace(/^Smart\s*FX\s*/i, "") : (license.tier ? license.tier.charAt(0).toUpperCase() + license.tier.slice(1) : "Pro"))
    : "Free";

  return (
    <div className="relative min-h-screen">
      {/* Hero background */}
      <div className="absolute top-0 inset-x-0 h-[560px] pointer-events-none">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(4,6,13,0.35) 0%, rgba(4,6,13,0.7) 55%, #04060d 100%)" }} />
      </div>

      <div className="relative z-10 px-5 pt-6 fade-up">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5">
            <span className="font-heading text-xl font-bold tracking-tight" data-testid="dashboard-username">
              {user?.username || user?.name || "Trader"}
            </span>
            {license?.verified && (
              <span data-testid="verified-badge" aria-label="Verified"
                className="inline-flex items-center justify-center h-5 w-5 rounded-full shrink-0"
                style={{ backgroundColor: "#1D9BF0" }}>
                <Check className="h-3 w-3 text-white" strokeWidth={3.5} />
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <span data-testid="license-badge" className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${license?.is_staff || license?.is_partner ? "text-white" : license?.active ? "bg-amber-400 text-black" : "bg-[#14233d] text-[#8fb4e6]"}`}
              style={license?.is_staff || license?.is_partner ? { backgroundColor: "#1C8CFF" } : undefined}>
              {license?.is_staff ? <ShieldCheck className="h-3 w-3" /> : license?.is_partner ? <BadgeCheck className="h-3 w-3" /> : <Crown className="h-3 w-3" />} {tier}
            </span>
            <NewsBell />
            <Dialog>
              <DialogTrigger asChild>
                <button data-testid="info-button" className="h-8 w-8 rounded-full border border-white/30 flex items-center justify-center text-white/80 hover:text-white hover:border-white transition-colors">
                  <Info className="h-4 w-4" />
                </button>
              </DialogTrigger>
              <DialogContent data-testid="account-info-fullscreen"
                className="bg-[#060c18] text-white border-0 p-0 gap-0 max-w-full w-screen h-[100dvh] left-0 top-0 translate-x-0 translate-y-0 rounded-none sm:rounded-none flex flex-col">
                <DialogHeader className="px-5 pt-6 pb-4 border-b border-[#14233d] shrink-0">
                  <DialogTitle className="flex items-center gap-2 font-heading text-lg">
                    <Info className="h-5 w-5" style={{ color: "var(--sfx-accent)" }} /> Account &amp; Bot Info
                  </DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto px-5 py-5 space-y-3 text-sm">
                  {[
                    ["Bot", bot?.name || "None"],
                    ["Strategy", bot?.strategy || "-"],
                    ["Win rate", `${bot?.win_rate ?? 0}%`],
                    ["Account", account ? (isProp ? account.firm_name : account.broker) : "Not connected"],
                    ["Plan", license?.active ? `${license.plan_name} · ${license.days_left}d` : "Inactive"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between items-center border border-[#14233d] bg-[#0A1120] rounded-xl px-4 py-3">
                      <span className="text-[#5f7ba6]">{k}</span>
                      <span className={k === "Win rate" ? "text-[#22e07b] font-semibold" : "font-semibold"}>{v}</span>
                    </div>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {!bot ? (
          <div className="border border-[#14233d] bg-[#0A1120] p-12 text-center rounded-3xl mt-24">
            <Cpu className="h-12 w-12 text-[#1C8CFF] mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold mb-1">No bot connected</h3>
            <p className="text-sm text-[#5f7ba6] mb-6">Add Smart Scalper Fx to start automated trading.</p>
            <Button onClick={addBot} data-testid="add-bot-button" disabled={busy === "add"}
              className="h-11 rounded-full px-6 text-white hover:opacity-90 font-semibold" style={{ backgroundColor: "var(--sfx-accent)" }}>
              {busy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Smart Scalper Fx"}
            </Button>
          </div>
        ) : (
          <>
            {/* Avatar */}
            <div className="flex flex-col items-center text-center mt-6">
              <div className="relative">
                <div className="absolute inset-0 rounded-full blur-2xl opacity-40" style={{ background: "radial-gradient(circle, var(--sfx-accent-glow), transparent 65%)" }} />
                <div className="relative h-28 w-28 rounded-full border-2 flex items-center justify-center overflow-hidden"
                  style={{ borderColor: "var(--sfx-accent)", boxShadow: "0 0 24px var(--sfx-accent-glow)", background: "radial-gradient(circle at 50% 40%, #0b1a2e, #04060d)" }}>
                  <img src={avatarSrc} alt={bot.name} data-testid="bot-avatar" className="h-full w-full object-cover" />
                </div>
              </div>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-5" data-testid="bot-name">{bot.name}</h1>
              <p className="text-white/90 text-base mt-4 leading-relaxed">
                intelligent, disciplined, and precise<br /><span className="font-semibold">POWERED BY TRZEFXPRO</span>
              </p>
              <div className="mt-4 flex items-center justify-center gap-2 flex-wrap" data-testid="dashboard-status-row">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${mt5Connected ? "border-[#22e07b]/40 bg-[#22e07b]/10 text-[#22e07b]" : "border-[#ff5b5b]/40 bg-[#ff5b5b]/10 text-[#ff5b5b]"}`} data-testid="mt5-status-pill">
                  <span className={`h-2 w-2 rounded-full ${mt5Connected ? "bg-[#22e07b]" : "bg-[#ff5b5b]"}`} />
                  {mt5Connected ? "MT5 Connected" : "MT5 Disconnected"}
                </span>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${running ? "border-[#22e07b]/40 bg-[#22e07b]/10 text-[#22e07b]" : "border-[#ff5b5b]/40 bg-[#ff5b5b]/10 text-[#ff5b5b]"}`} data-testid="bot-status-pill">
                  <span className={`h-2 w-2 rounded-full ${running ? "bg-[#22e07b] live-dot" : "bg-[#ff5b5b]"}`} />
                  {running ? "Bot Running" : "Bot Stopped"}
                </span>
              </div>
            </div>

            {/* Action cluster */}
            <div className="relative w-full mt-8 mb-4">
              <div className="flex items-center justify-between bg-[#060c18]/90 border-2 rounded-full px-8 py-4"
                style={{ borderColor: "var(--sfx-accent)", boxShadow: "0 0 40px var(--sfx-accent-glow)" }}>
                <SideAction icon={LineChart} label="PAIRS" onClick={() => navigate("/app/pairs")} testid="pairs-action" />
                <div className="w-16 shrink-0" />
                <SideAction icon={History} label="LOGS" onClick={openLogs} testid="logs-action" />
              </div>
              <button onClick={toggle} data-testid="start-stop-button" disabled={busy === "toggle"}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-16 w-16 rounded-full flex flex-col items-center justify-center transition-transform duration-200 hover:scale-105 active:scale-95"
                style={{ backgroundColor: "var(--sfx-accent)", boxShadow: "0 0 28px var(--sfx-accent-glow)" }}>
                {busy === "toggle" ? <Loader2 className="h-4 w-4 text-white animate-spin" /> : (
                  <>
                    {running ? <Square className="h-4 w-4 text-white" fill="white" /> : <Play className="h-4 w-4 text-white" fill="white" />}
                    <span className="font-heading font-bold text-white text-[9px] tracking-[0.12em] mt-0.5">{running ? "STOP" : "START"}</span>
                  </>
                )}
              </button>
            </div>

            {/* Connected account card */}
            {account && (
              <button onClick={() => navigate("/app/account")} data-testid="account-card"
                className="w-full text-left border border-[#14233d] bg-gradient-to-b from-[#0B1526] to-[#0A1120] rounded-3xl p-5 mt-6 flex items-center justify-between hover:border-[#1C8CFF]/50 transition-colors">
                <div>
                  <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">Connected Account</div>
                  <div className="font-heading font-bold mt-0.5">{isProp ? account.firm_name : account.broker}</div>
                  <div className="font-mono-term text-xs text-[#7f9bc4] mt-0.5">#{account.account_id} · {account.server}</div>
                </div>
                <ArrowRight className="h-5 w-5 text-[#5f7ba6]" />
              </button>
            )}

            {/* MT5 not linked -> show WHY + a retry button */}
            {account && !mt5Connected && (
              <div className="border border-[#f5a623]/40 bg-[#f5a623]/5 rounded-3xl p-5 mt-4" data-testid="mt5-reconnect-card">
                <div className="flex items-start gap-3">
                  <TriangleAlert className="h-5 w-5 text-[#f5a623] shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-[#f5c877] text-sm">MT5 pa konekte</div>
                    <p className="text-[12px] text-[#e8d3a8] leading-relaxed mt-1" data-testid="mt5-connect-error">
                      {account.connect_error ||
                        "MetaAPI pa t ka konekte kont lan. Tcheke non SÈVÈ a EGZAK jan li parèt nan MT5 ou (egz. \"JustMarkets-Live7\") ak MASTER password ou (pa investor)."}
                    </p>
                  </div>
                </div>
                <button onClick={reconnect} disabled={busy === "reconnect"} data-testid="mt5-reconnect-button"
                  className="mt-3 w-full h-11 rounded-2xl bg-[#f5a623] text-black font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy === "reconnect" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Rekonekte MT5"}
                </button>
                <button onClick={() => navigate("/app/account")} data-testid="mt5-edit-account-button"
                  className="mt-2 w-full h-9 rounded-2xl border border-[#14233d] text-[#8fb4e6] text-xs font-semibold">
                  Modifye login / sèvè / modpas
                </button>
              </div>
            )}

            {daily?.available && (
              <div className="border border-[#14233d] bg-gradient-to-b from-[#0B1526] to-[#0A1120] rounded-3xl p-5 mt-4" data-testid="daily-report-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">Today's Result</div>
                  <div className="font-heading text-2xl font-bold tabular-nums" data-testid="daily-pnl"
                    style={{ color: daily.daily_pnl > 0 ? "#22e07b" : daily.daily_pnl < 0 ? "#ff5b5b" : "#8fb4e6" }}>
                    {daily.daily_pnl > 0 ? "+" : ""}{daily.daily_pnl.toFixed(2)} <span className="text-xs text-[#5f7ba6]">{daily.currency}</span>
                  </div>
                </div>
                {daily.target > 0 && (
                  <div className="mb-3">
                    <div className="flex justify-between font-mono-term text-[10px] text-[#5f7ba6] mb-1">
                      <span>Profit Target</span>
                      <span data-testid="daily-target-progress">{daily.daily_pnl > 0 ? daily.daily_pnl.toFixed(0) : 0} / ${daily.target.toFixed(0)} · {daily.target_progress}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#060c18] overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${daily.target_progress}%`, backgroundColor: "#22e07b" }} />
                    </div>
                    {daily.target_progress >= 100 && <div className="mt-1.5 text-[11px] font-semibold text-[#22e07b]">🎯 Objektif jodi a rive — bot la sispann pou jounen an!</div>}
                  </div>
                )}
                {daily.loss_limit > 0 && (
                  <div>
                    <div className="flex justify-between font-mono-term text-[10px] text-[#5f7ba6] mb-1">
                      <span>Daily Loss Limit</span>
                      <span data-testid="daily-loss-progress">{daily.daily_pnl < 0 ? (-daily.daily_pnl).toFixed(0) : 0} / ${daily.loss_limit.toFixed(0)} · {daily.loss_progress}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#060c18] overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${daily.loss_progress}%`, backgroundColor: "#ff5b5b" }} />
                    </div>
                  </div>
                )}
                {daily.target <= 0 && daily.loss_limit <= 0 && (
                  <button onClick={() => navigate("/app/pairs")} className="font-mono-term text-[11px] text-[#1C8CFF] hover:underline">
                    + Mete yon Daily Profit Target nan Pairs
                  </button>
                )}
              </div>
            )}

            {license && !license.active && (
              <div data-testid="license-banner" className="flex items-center justify-between gap-3 border border-[#ff5b5b]/40 bg-[#ff5b5b]/10 px-4 py-4 rounded-2xl mt-4">
                <div className="flex items-center gap-3">
                  <TriangleAlert className="h-5 w-5 text-[#ff5b5b] shrink-0" />
                  <div>
                    <div className="font-semibold text-sm">{license.status === "expired" ? "Subscription expired" : "No active subscription"}</div>
                    <div className="text-xs text-[#a9c4ec]">AutoTrade is disabled.</div>
                  </div>
                </div>
                <Link to="/app/billing"><Button className="h-9 rounded-full px-4 text-white font-semibold" style={{ backgroundColor: "var(--sfx-accent)" }}>Upgrade</Button></Link>
              </div>
            )}

            {isProp && (
              <div className="mt-6" data-testid="propfirm-metrics">
                {(() => {
                  const lim = account.propfirm_limits || {};
                  const snap = account.propfirm_snapshot || {};
                  const dllLeft = Math.max(0, (lim.daily_loss_limit || 0) - (snap.daily_loss || 0));
                  const ddLeft = Math.max(0, (lim.max_drawdown || 0) - (snap.drawdown || 0));
                  return (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <h2 className="font-heading text-base font-semibold">{account.firm_name}</h2>
                        <span className="font-mono-term text-[10px] uppercase tracking-widest text-[#8fb4e6] border border-[#14233d] px-2 py-1 rounded-full">{lim.label || account.challenge_phase || "Evaluation"}</span>
                      </div>
                      {account.propfirm_block_reason && (
                        <div className="mb-3 flex items-start gap-2 border border-[#ff5b5b]/40 bg-[#ff5b5b]/10 px-4 py-3 rounded-2xl" data-testid="propfirm-block-banner">
                          <ShieldAlert className="h-4 w-4 text-[#ff5b5b] shrink-0 mt-0.5" />
                          <p className="text-xs text-[#ffd0d0] leading-relaxed">{account.propfirm_block_reason}</p>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <Metric icon={Target} label="Profit Target" value={lim.profit_target ? `$${lim.profit_target.toLocaleString()}` : "—"} />
                        <Metric icon={Percent} label="Current Profit" value={`$${(snap.profit || 0).toLocaleString()}`} tone={snap.profit >= 0 ? "up" : "down"} />
                        <Metric icon={TrendingDown} label="Daily Loss Left" value={`$${dllLeft.toLocaleString()}`} />
                        <Metric icon={ShieldAlert} label="Max Drawdown Left" value={`$${ddLeft.toLocaleString()}`} />
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {safety && (
              <div className="rounded-3xl border border-[#14233d] bg-[#0A1120] p-5" data-testid="daily-safety-card">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
                  <span className="font-heading font-semibold text-sm">Rezime Sekirite Jodi a</span>
                  <span className="ml-auto font-mono-term text-[9px] text-[#5f7ba6]">{safety.date}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-[#14233d] bg-[#060c18] p-3" data-testid="summary-trades">
                    <div className="font-mono-term text-[9px] uppercase tracking-widest text-[#5f7ba6]">Trade louvri</div>
                    <div className="font-heading text-xl font-bold text-white mt-1">{safety.trades_opened}</div>
                  </div>
                  <div className="rounded-2xl border border-[#14233d] bg-[#060c18] p-3" data-testid="summary-protections">
                    <div className="font-mono-term text-[9px] uppercase tracking-widest text-[#5f7ba6]">Pwoteksyon deklanche</div>
                    <div className="font-heading text-xl font-bold mt-1" style={{ color: safety.protections_triggered ? "#f5a623" : "#22e07b" }}>{safety.protections_triggered}</div>
                  </div>
                </div>
                {safety.propfirm && safety.propfirm.remaining_daily != null && (
                  <div className="mt-3 rounded-2xl border border-[#14233d] bg-[#060c18] p-3 flex items-center gap-2" data-testid="summary-propfirm">
                    <TrendingDown className="h-4 w-4 text-[#8fb4e6] shrink-0" />
                    <span className="text-[11px] text-[#cfe0f5]">
                      Espas sekirite prop firm ki rete jodi a: <b className="text-white">${(safety.propfirm.remaining_daily || 0).toLocaleString()}</b>
                      {safety.propfirm.safety_stop_pct ? ` (tanpon ${safety.propfirm.safety_stop_pct}%)` : ""}
                    </span>
                  </div>
                )}
                <p className="text-[10px] text-[#5f7ba6] mt-3 leading-relaxed">Nou voye rezime sa a ba ou chak swa pa imèl ak notifikasyon tou.</p>
              </div>
            )}

            <AISoundCard />
          </>
        )}
      </div>
    </div>
  );
}
